/**
 * MonthStrip — 12 horizontal month cells with temperature range + sunshine
 * indicator.  Used in climate articles; works as the `annual-timeline`
 * widget kind from INTERACTIVE.md.
 *
 * Mobile: horizontal scroll (per ARCHITECTURE §4.4 — chart_strategy
 * `horizontal-scroll`).  No tap state required because all the data is
 * on-screen at once; tooltips are kept off-by-default to avoid
 * scroll-snags on touch.
 *
 * Honors `prefers-reduced-motion: reduce` — the entry animation is gated
 * via Tailwind `motion-safe:` / `motion-reduce:`.
 */

import { useState } from 'react';
import { cn } from '@/components/ui/cn';
import type { LangCode } from '../architecture';

export interface MonthData {
  /** Three-letter month label, lang-specific (Янв / Jan) */
  m: string;
  /** Average daily high °C */
  high: number;
  /** Average daily low °C */
  low: number;
  /** Daily sunshine hours */
  sun: number;
  /** Optional note (rendered as tooltip + below detail) */
  note?: string;
}

interface MonthStripProps {
  data: MonthData[];
  lang: LangCode;
  /** Optional caption rendered above the strip. */
  caption?: string;
  /** Range for normalising temp bars; auto from data when omitted. */
  tempRange?: [min: number, max: number];
}

export function MonthStrip({ data, lang, caption, tempRange }: MonthStripProps) {
  const [active, setActive] = useState<number | null>(null);

  // Establish a stable temperature range so all bars use the same scale.
  const allTemps = data.flatMap((d) => [d.high, d.low]);
  const min = tempRange?.[0] ?? Math.min(...allTemps, -5);
  const max = tempRange?.[1] ?? Math.max(...allTemps, 35);
  const span = max - min;

  const sunMax = Math.max(...data.map((d) => d.sun), 12);

  // Project temperature → 0..1 normalised within the strip
  const norm = (t: number) => (t - min) / span;

  return (
    <figure className="my-6 sm:my-8">
      {caption && (
        <figcaption className="mb-3 font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {caption}
        </figcaption>
      )}

      {/* Horizontal-scroll container.  Edge gradient mask + scrollbar
          signal scrollability on mobile.  Desktop: 12 cells fit in 720px. */}
      <div
        role="group"
        aria-label={lang === 'ru' ? 'Климат по месяцам' : 'Monthly climate'}
        className="relative overflow-x-auto -mx-4 px-4 [scrollbar-color:var(--color-ink-300)_transparent] [scrollbar-width:thin]"
      >
        <div className="mx-auto grid min-w-[640px] grid-cols-12 gap-1 rounded-lg border border-ink-100 bg-paper-2 p-3 sm:min-w-0">
          {data.map((d, i) => {
            const isActive = active === i;
            // Bar height in % of cell — high − low spans the temperature range
            const top = (1 - norm(d.high)) * 100;
            const bottom = (1 - norm(d.low)) * 100;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActive(isActive ? null : i)}
                aria-pressed={isActive}
                className={cn(
                  'group relative flex flex-col items-stretch rounded-md px-1.5 py-2 text-left transition-colors motion-reduce:transition-none',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-terra-600/60',
                  isActive
                    ? 'bg-paper'
                    : 'hover:bg-paper',
                )}
                title={d.note}
              >
                {/* Sunshine pip — tiny dot row at top */}
                <span
                  aria-hidden="true"
                  className="mb-1 flex h-1.5 w-full items-center justify-center gap-[2px]"
                >
                  {Array.from({ length: Math.max(1, Math.round((d.sun / sunMax) * 6)) }).map((_, k) => (
                    <span key={k} className="h-1.5 w-1.5 rounded-full bg-ochre-500" />
                  ))}
                </span>

                {/* Temperature bar — vertical, terra-600 high → moss-600 low */}
                <span
                  aria-hidden="true"
                  className="relative my-1 block h-24 w-full overflow-hidden rounded bg-paper"
                >
                  <span
                    className="absolute inset-x-1.5 rounded bg-gradient-to-b from-rust-500/85 via-ochre-500/70 to-terra-600/85 motion-safe:transition-all"
                    style={{ top: `${top}%`, bottom: `${bottom}%` }}
                  />
                </span>

                {/* High / low labels */}
                <span className="mt-1 flex items-baseline justify-between font-mono text-[10px] tabular-nums">
                  <span className="text-terra-700">{Math.round(d.low)}°</span>
                  <span className="text-rust-600">{Math.round(d.high)}°</span>
                </span>

                {/* Month label */}
                <span className="mt-0.5 text-center font-mono text-[10px] uppercase tracking-wider text-ink-500">
                  {d.m}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active month detail (or hint) */}
      <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-wider text-ink-400">
        {active != null && data[active]?.note
          ? data[active].note
          : lang === 'ru'
          ? `Точки сверху — солнечные часы · нажми ячейку`
          : `Pips above — sunshine hours · tap a cell`}
      </p>
    </figure>
  );
}
